import React, { memo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ID_CARD_LIST, DRIVERNUMBER_REGION_LIST } from '@common/constants';

import { isEmptyString, isFunction, isSuccessStatus } from '@common/utils';
import { getDateByformat } from '@common/utils/date';

import SatDataGrid from '@components/common/satUI/SatDataGrid';
import SatButton from '@components/common/satUI/SatButton';
import SatModal from '@components/common/satUI/SatModal';
import SatInput from '@components/common/satUI/SatInput';
import SatSelect from '@components/common/satUI/SatSelect';
import SecurityInput from '@components/common/biz/SecurityInput';
import { useDispatch } from 'react-redux';
import { renewCustomerSuitability } from '@store/actions/customerAction';

/**
 * SatModal이 모달위에 모달 표기 시, 부모 모달영역을 넘지 못하는 이슈로 추가
 */
const SlideModal = ({ visible, handleCloseButtonClick, children, ...rest }) => {
  return (
    visible && (
      <>
        <div
          className={`modal slide layer-slide ${visible ? 'open' : ''}`}
          style={{ display: visible ? 'block' : 'none', overflow: 'visible' }}
          {...rest}
        >
          {children}
          <span className="btn-layer-close" onClick={handleCloseButtonClick} />
        </div>

        <div className="dim" style={{ display: visible ? 'block' : 'none' }} />
      </>
    )
  );
};

SlideModal.propTypes = {
  visible: PropTypes.bool,
  handleCloseButtonClick: PropTypes.func,
  children: PropTypes.node.isRequired,
};

SlideModal.defaultProps = {
  visible: false,
};

/**
 * 운전면허증
 */
const DriverLicenseCont = ({
  certiForm,
  setFormProps,
  idCardList,
  driverNumberRegionList,
}) => {
  const registerDriverNumberKey = 'suitabilityRegisterDriverNumberKey';
  const { regionCodes } = $hooks.useConstant();

  const handleChange = value => {
    /* @NOTE: 보안 키패드 사용 여부 값에 따른 처리 */
    if ($utils.checkSecureKeypad()) {
      setFormProps('driveNumberSecurityObj', value);
    } else {
      setFormProps('idCardNo', value);
    }
    setFormProps('reqType', 'D');
  };

  return (
    <div
      className={`tab-con ${
        certiForm.idCardType ===
        ID_CARD_LIST.find(a => '운전면허증' === a.label).value
          ? 'active'
          : null
      }`}
      id="con-tab2"
    >
      <div className="box">
        <SatSelect
          title="지역번호선택"
          label={'지역번호선택'}
          value={certiForm.regionCode}
          border
          options={regionCodes}
          customStyleClass={'license01'}
          onChange={({ value }) => {
            setFormProps('regionCode', value);
            setFormProps(
              'reqType',
              idCardList.find(a => '운전면허증' === a.label).value,
            );
          }}
        />

        {/* @NOTE: 보안 키패드 사용 여부 값에 따른 처리 */}
        {$utils.checkSecureKeypad() ? (
          <SecurityInput
            label="면허번호"
            customStyleClass={'license02'}
            value={certiForm?.driveNumberSecurityObj?.value}
            encryptionKey={registerDriverNumberKey}
            maxLength="10"
            onChange={value => handleChange(value)}
          />
        ) : (
          <SatInput
            border
            isPrivacy
            isRandomPad
            type="number"
            label="면허번호"
            maxLength="10"
            customStyleClass="input-wrap license02"
            value={certiForm.idCardNo}
            onChange={value => handleChange(value)}
          />
        )}
      </div>
      <p className="text-info ico-type">
        2014년 7월 14일 이전은 지역명과 숫자를 확인하고, 이후 면허는 숫자를
        확인해주세요.
      </p>
    </div>
  );
};

DriverLicenseCont.propTypes = {
  certiForm: PropTypes.shape({
    idCardType: PropTypes.string.isRequired,
    regionCode: PropTypes.string.isRequired,
    idCardNo: PropTypes.string.isRequired,
    reqType: PropTypes.string.isRequired,
  }).isRequired,
  setFormProps: PropTypes.func.isRequired,
  idCardList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  driverNumberRegionList: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * 주민등록증
 */
const RsdnCont = ({ certiForm, setFormProps, idCardList }) => (
  <div
    className={`tab-con ${
      certiForm.idCardType ===
      idCardList.find(a => '주민등록증' === a.label).value
        ? 'active'
        : null
    }`}
    id="con-tab1"
  >
    <div className="box">
      <SatInput
        value={certiForm.issueDateR}
        label="발급일자"
        type="number"
        border
        maxLength={8}
        useDate
        onChange={issueDateR => {
          setFormProps('issueDateR', issueDateR);
          setFormProps('reqType', 'I');
        }}
      />
    </div>
    <p className="text-info2 ico-type">
      기관 정보 제외, 발급 일자는 숫자로만 작성해주세요.
    </p>
  </div>
);

RsdnCont.propTypes = {
  certiForm: PropTypes.shape({
    idCardType: PropTypes.string.isRequired,
    issueDateR: PropTypes.string.isRequired,
    reqType: PropTypes.string.isRequired,
  }).isRequired,
  setFormProps: PropTypes.func.isRequired,
  idCardList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * 외국인등록증
 */
const ForeignerRegisterCardCont = ({ certiForm, idCardList, setFormProps }) => (
  <div
    className={`tab-con ${
      certiForm.idCardType ===
      idCardList.find(a => '외국인등록증' === a.label).value
        ? 'active'
        : null
    }`}
    id="con-tab3"
  >
    <div className="box">
      <SatInput
        value={certiForm.issueDateF}
        label="발급일자"
        type="number"
        border
        maxLength={8}
        useDate
        onChange={issueDateF => {
          setFormProps('issueDateF', issueDateF);
          setFormProps(
            'reqType',
            idCardList.find(a => '외국인등록증' === a.label).value,
          );
        }}
      />
    </div>
    <p className="text-info2 ico-type">
      기관 정보 제외, 발급 일자는 숫자로만 작성해주세요.
    </p>
  </div>
);

ForeignerRegisterCardCont.propTypes = {
  certiForm: PropTypes.shape({
    idCardType: PropTypes.string.isRequired,
    issueDateF: PropTypes.string.isRequired,
    reqType: PropTypes.string.isRequired,
  }).isRequired,
  idCardList: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  setFormProps: PropTypes.func.isRequired,
};

/**
 *  변액 적합성진단 변경 이력 목록
 */
const SuitabilityTestHistoryPopup = ({
  customerInfo,
  dataSource,
  visible,
  setShow,
  showModal,
  checkIdentityCard,
  // searchCustomerByCustNo,
  // setCustomerState,
  isUsd,
}) => {
  const dispatch = useDispatch();

  const [isShowCertiPopup, setShowCertiPopup] = useState(false);
  const [certiForm, setCertiForm] = useState({
    name: customerInfo.name,
    custNo: customerInfo.custNo,
    mobileTelNo: customerInfo.mobileTelNo,
    bizKind: isUsd ? '08' : '07', // 외화: 08, 변액: 07
    idCardType: 'D', // 기본값 운전면허증
    regionCode: '',
    idCardNo: '',
    reqType: 'D',
    issueDateR: '',
    issueDateF: '',
    applicationNo: '',
    reportSerialNo: '',
    driveNumberSecurityObj: {},
  });
  const [columns] = useState([
    {
      title: '번호',
      width: '8%',
      dataIndex: 'rowIndex',
      render: value => <>{value + 1}</>,
    },
    {
      title: '진단일자',
      width: '12%',
      dataIndex: 'testDate',
      render: value => (
        <span className="t-date">
          {!isEmptyString(value) ? getDateByformat(value, 'yyyy.MM.dd') : '-'}
        </span>
      ),
    },
    {
      title: '상품성향/투자성향',
      dataIndex: 'prdTypeText',
      render: (_, row) => {
        const getText = (a, b) => {
          if ([a, b].every(t => !isEmptyString(t))) {
            return `${a} / ${b}`;
          } else if ([a, b].some(t => !isEmptyString(t))) {
            return `${[a, b].find(t => !isEmptyString(t))}`;
          } else {
            return '-';
          }
        };

        const getBfText = (a, b) => {
          const text = getText(a, b);
          return '-' === text ? '' : `(이전 : ${text})`;
        };

        return (
          <>
            {`${getText(row.prdTypeText, row.invTypeText)}`}
            {!isEmptyString(
              getBfText(row.bfPrdTypeText, row.bfInvTypeText),
            ) && (
              <>
                <br />
                <span className="f-12">{`${getBfText(
                  row.bfPrdTypeText,
                  row.bfInvTypeText,
                )}`}</span>
              </>
            )}
          </>
        );
      },
    },
    {
      title: '발행여부',
      width: '8%',
      dataIndex: 'printYn',
      render: value => <>{'Y' === value ? '발행됨' : '-'}</>,
    },
    {
      title: '진단지번호',
      width: '12%',
      dataIndex: 'reportSerialNo',
      render: value => <>{!isEmptyString(value) ? value : '-'}</>,
    },
    {
      title: '진단상태',
      width: '16%',
      dataIndex: 'suitabilityStatusText',
      render: value => <>{!isEmptyString(value) ? value : '-'}</>,
    },
    {
      title: '접수일자',
      width: '12%',
      dataIndex: 'completeDate',
      render: value => (
        <>
          {!isEmptyString(value) ? getDateByformat(value, 'yyyy.MM.dd') : '-'}
        </>
      ),
    },
    {
      title: '재인증',
      width: '10%',
      dataIndex: 'suitabilityStatus',
      render: (_, row) => (
        <>
          {'인증실패(신분증)' === (row.suitabilityStatusText ?? '') ? (
            <SatButton
              label="인증필요"
              styleClass="btn-s b-blue"
              onClick={() => {
                setCertiForm(prev =>
                  Object.assign({}, prev, {
                    applicationNo: row.applicationNo ?? '',
                    reportSerialNo: row.reportSerialNo ?? '',
                  }),
                );
                setShowCertiPopup(true);
              }}
            />
          ) : (
            <>-</>
          )}
        </>
      ),
    },
  ]);

  /**
   * formData 설정 helper
   */
  const setFormProps = useCallback(
    (setFormData => (path, value) => {
      const setNestedValue = (obj, path, value) => {
        const clone = JSON.parse(JSON.stringify(obj));
        const pathes = path.split('.');

        pathes.reduce((acc, v, i) => {
          if (pathes.length === i + 1) {
            acc[v] = value; // mutation
          }
          return acc[v];
        }, clone);

        return clone;
      };

      setFormData(prev => setNestedValue(prev, path, value));
    })(setCertiForm),
    [],
  );

  /**
   * 진단이력 팝업, 신분증 인증 팝업 같이 닫기
   */
  const closeModals = useCallback(() => {
    setShowCertiPopup(false);
    setShow(false);
  }, [setShow, setShowCertiPopup]);

  /**
   * data-tab 번호 조회 (필요한지 의문...)
   */
  const getTabNum = useCallback(label => {
    return {
      운전면허증: 2,
      주민등록증: 1,
      외국인등록증: 3,
    }[label];
  }, []);

  /**
   * 신분증인증 폼데이터 유효성 검사
   */
  const isValidFormData = useCallback(certiForm => {
    const {
      idCardType,
      regionCode,
      idCardNo,
      issueDateR,
      issueDateF,
      driveNumberSecurityObj,
    } = certiForm;
    let isValid = false;
    if (idCardType === 'D') {
      isValid =
        !!regionCode &&
        /* @NOTE: 보안 키패드 사용 여부 값에 따른 처리 */
        (($utils.checkSecureKeypad() &&
          driveNumberSecurityObj?.value?.length === 10) ||
          (!$utils.checkSecureKeypad() && idCardNo?.length === 10));
    } else if (idCardType === 'R') {
      isValid = !isEmptyString(issueDateR);
    } else if (idCardType === 'F') {
      isValid = !isEmptyString(issueDateF);
    }

    return isValid;
  }, []);

  /**
   * 인증요청
   */
  const handleSubmit = useCallback(
    (certiForm, closeModals) => e => {
      // 이벤트 사이드 이펙트 제거
      const removeEventEffect = e => {
        isFunction(e.preventDefault) && e.preventDefault();
        isFunction(e.stopPropagation) && e.stopPropagation();
      };
      removeEventEffect(e);

      checkIdentityCard({
        name: certiForm.name,
        custNo: certiForm.custNo,
        bizKind: certiForm.bizKind,
        idCardType: certiForm.idCardType,
        issueDateR: certiForm.issueDateR,
        issueDateF: certiForm.issueDateF,
        regionCode: certiForm.regionCode,
        idCardNo: certiForm.idCardNo,
        mobileTelNo: certiForm.mobileTelNo,
        applicationNo: certiForm.applicationNo,
        reportSerialNo: certiForm.reportSerialNo,
        driveNumberSecurityObj: certiForm.driveNumberSecurityObj,
        callback: response => {
          const { status } = response;
          const getAlertMsg = status => {
            if (isSuccessStatus(status)) {
              return '개인정보 동의 및 본인인증이 완료되었습니다.';
            } else {
              const errorMessage = $utils.getApiErrorMessage(
                response,
                '신분증 인증에 실패했습니다.',
              );
              return errorMessage;
            }
          };
          showModal({
            visible: true,
            type: 'info',
            msg: getAlertMsg(status),
          });

          if (isSuccessStatus(status)) {
            // Robin: 재인증 성공 시, 고객정보 재조회 하지 않고, 적합성 정보만 갱신
            dispatch(
              renewCustomerSuitability({
                custNo: certiForm.custNo,
                isUsd,
                callback: closeModals,
              }),
            );

            // // 고객정보 재조회
            // searchCustomerByCustNo({
            //   custNo: certiForm.custNo,
            //   callback: data => {
            //     setCustomerState({ customerInfo: data });
            //   },
            // });
          }
        },
      });
    },
    [],
  );

  useEffect(() => {
    const cleanUp = () => {
      setCertiForm(() => ({
        idCardType: 'D', // 기본값 운전면허증
        regionCode: '',
        idCardNo: '',
        reqType: 'D',
        issueDateR: '',
        issueDateF: '',
      }));
    };
    return cleanUp;
  }, []);

  /**
   * 대상 신분증 변경 시, 초기화
   */
  useEffect(() => {
    certiForm.regionCode = '';
    certiForm.idCardNo = '';
    certiForm.issueDateR = '';
    certiForm.issueDateF = '';
  }, [certiForm.idCardType]);

  return (
    <>
      <SatModal
        visible={visible}
        type="fullPop"
        setVisible={() => setShow(false)}
      >
        <header className="con-header">
          <div className="page-navi">
            <SatButton
              label="이전"
              styleClass="btn-back"
              onClick={() => closeModals()}
            />
          </div>
          <h1 className="page-tit">{`${
            isUsd ? '외화 ' : ''
          }적합성 진단 변경이력`}</h1>
        </header>

        <div className="con-wrap no-bottom">
          <div className="pop-box2">
            <SatDataGrid
              columns={columns}
              dataSource={dataSource}
              customStyleClass="bd-b-fix2"
              setRowStyle="text-center"
            />
          </div>
        </div>

        {/* 신분증 인증 팝업 */}
        {isShowCertiPopup && (
          <SlideModal
            visible={isShowCertiPopup}
            handleCloseButtonClick={() => setShowCertiPopup(false)}
          >
            <form onSubmit={e => e.preventDefault()}>
              <h3 className="layer-tit2">신분증 인증 요청</h3>
              <div className="cont">
                <div className="tab-wrap mgt-16">
                  <ul className="tab-nav">
                    {ID_CARD_LIST.map((v, i) => (
                      <li
                        key={`con-tab-${i}`}
                        data-tab={`con-tab${getTabNum(v.label)}`}
                        className={
                          certiForm.idCardType === v.value ? 'active' : null
                        }
                      >
                        <span
                          className="tab"
                          onClick={() => setFormProps('idCardType', v.value)}
                        >
                          {v.label}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="h-97">
                    {/* 운전면허증 */}
                    <DriverLicenseCont
                      certiForm={certiForm}
                      idCardList={ID_CARD_LIST}
                      driverNumberRegionList={DRIVERNUMBER_REGION_LIST}
                      setFormProps={setFormProps}
                    />
                    {/* 주민등록증 */}
                    <RsdnCont
                      certiForm={certiForm}
                      setFormProps={setFormProps}
                      idCardList={ID_CARD_LIST}
                    />
                    {/* 외국인등록증 */}
                    <ForeignerRegisterCardCont
                      certiForm={certiForm}
                      idCardList={ID_CARD_LIST}
                      setFormProps={setFormProps}
                    />
                  </div>
                </div>
              </div>
              <div className="mgt-16">
                <button
                  type="button"
                  className="btn-l blue"
                  disabled={!isValidFormData(certiForm)}
                  onClick={handleSubmit(certiForm, closeModals)}
                >
                  인증요청
                </button>
              </div>
              <button
                type="button"
                className="btn-layer-close modal-close"
                onClick={() => setShowCertiPopup(false)}
              >
                창닫기
              </button>
            </form>
          </SlideModal>
        )}
      </SatModal>
    </>
  );
};

SuitabilityTestHistoryPopup.propTypes = {
  customerInfo: PropTypes.shape({
    name: PropTypes.string.isRequired,
    custNo: PropTypes.oneOfType([
      PropTypes.number.isRequired,
      PropTypes.string.isRequired,
    ]),
  }),
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      prdTypeCode: PropTypes.string, // 상품성향코드
      invTypeCode: PropTypes.string, // 투자성향 코드
      suitabilityStatus: PropTypes.string, // 적합성진단 상태
      prdTypeCodeDate: PropTypes.string,
      invTypeCodeDate: PropTypes.string,
      reportSerialNo: PropTypes.string, // 진단지 시리얼번호
      prdTypeText: PropTypes.string, // 상품성향
      invTypeText: PropTypes.string, // 투자성향
      suitabilityStatusText: PropTypes.string, // 인증상태
      testDate: PropTypes.string, // 진단일
      applicationNo: PropTypes.string, // 발행번호
      bfPrdTypeText: PropTypes.string, // 이전 상품상향
      bfInvTypeText: PropTypes.string, // 이전 투자성향
      printYn: PropTypes.oneOf(['Y', 'N']), // 발행여부
      completedDate: PropTypes.string, // 접수일자
      isNeededAuthYn: PropTypes.oneOf(['Y', 'N']), // 재인증 필요여부
    }),
  ),
  visible: PropTypes.bool,
  setShow: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  checkIdentityCard: PropTypes.func.isRequired,
  searchCustomerByCustNo: PropTypes.func.isRequired,
  setCustomerState: PropTypes.func.isRequired,
  isUsd: PropTypes.bool, // 외화 적합성
};

SuitabilityTestHistoryPopup.defaultProps = {
  visible: false,
};

export default memo(SuitabilityTestHistoryPopup);
